import { useState, useEffect, useRef } from 'react';
import { Search, X, User, Loader2 } from 'lucide-react';
import { workforceService } from '../../services/workforceService';
import { jobService } from '../../services/jobService';
import { cn } from '../../utils/cn';

export default function InstanceContractorPopover({
  instanceId,
  instanceDisplayId,
  currentContractor,
  companyId,
  onAssigned,
  onClose,
  position, // { top, left } for positioning
  isBulk = false, // true when assigning to multiple selected instances
  selectedInstanceIds = [] // array of instance IDs for bulk assignment
}) {
  const [contractors, setContractors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);
  const popoverRef = useRef(null);

  // Load contractors on mount
  useEffect(() => {
    if (companyId) {
      loadContractors();
    }
  }, [companyId]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e?.target)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape') {
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const loadContractors = async () => {
    try {
      const data = await workforceService?.getCompanyContractors(companyId);
      setContractors(data || []);
    } catch (err) {
      console.error('Failed to load contractors:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssign = async (contractor) => {
    if (isAssigning) return;

    setIsAssigning(true);
    try {
      const contractorId = contractor?.userId || contractor?.id;

      console.log('=== CONTRACTOR ASSIGNMENT DEBUG ===');
      console.log('Contractor object:', contractor);
      console.log('ContractorId to use:', contractorId);
      console.log('InstanceId:', instanceId);
      console.log('isBulk:', isBulk);
      console.log('selectedInstanceIds:', selectedInstanceIds);

      let result = null;

      if (isBulk && selectedInstanceIds?.length > 0) {
        // Bulk assign to all selected instances
        const results = await Promise.all(
          selectedInstanceIds.map(id => {
            console.log('Bulk assigning to instance:', id);
            return jobService.assignContractor(id, contractorId);
          })
        );
        console.log('Bulk assignment results:', results);
        result = results;
      } else if (instanceId) {
        // Single instance assignment
        console.log('Calling jobService.assignContractor with:', { instanceId, contractorId });
        result = await jobService.assignContractor(instanceId, contractorId);
        console.log('Single assignment result:', result);
      } else {
        console.error('ERROR: No instanceId provided for assignment!');
        alert('Error: No instance ID provided');
        return;
      }

      if (result) {
        console.log('Assignment successful, calling onAssigned');
        onAssigned?.(contractor);
        onClose?.();
      } else {
        console.error('Assignment returned null/undefined result');
        alert('Assignment failed - no result returned');
      }
    } catch (err) {
      console.error('Failed to assign contractor:', err);
      console.error('Error details:', {
        message: err?.message,
        code: err?.code,
        details: err?.details,
        hint: err?.hint
      });
      alert(`Failed to assign: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsAssigning(false);
    }
  };

  const filteredContractors = contractors?.filter(c =>
    c?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    c?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  ) || [];

  // Calculate position to ensure popover stays in viewport
  const popoverStyle = {
    position: 'fixed',
    top: position?.top || 0,
    left: position?.left || 0,
    zIndex: 9999
  };

  return (
    <div
      ref={popoverRef}
      style={popoverStyle}
      className="w-72 bg-background border border-border rounded-lg shadow-xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="text-sm font-medium text-text-primary">
          {isBulk
            ? `Assign to ${selectedInstanceIds?.length || 0} instances`
            : `Assign to ${instanceDisplayId || 'Instance'}`
          }
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-text-secondary hover:text-text-primary transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="p-2 border-b border-border">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value || '')}
            placeholder="Search contractors..."
            autoFocus
            className="w-full pl-8 pr-3 py-1.5 bg-surface border border-border rounded text-sm text-text-primary placeholder-gray-500 focus:outline-none focus:border-primary"
          />
        </div>
      </div>

      {/* Contractor List */}
      <div className="max-h-48 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-center text-text-secondary">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
            Loading...
          </div>
        ) : filteredContractors?.length === 0 ? (
          <div className="p-4 text-center text-text-secondary text-sm">
            {searchTerm ? 'No contractors found' : 'No contractors available'}
          </div>
        ) : (
          filteredContractors?.map(contractor => {
            const contractorId = contractor?.userId || contractor?.id;
            const isCurrentContractor = currentContractor?.id === contractorId;

            return (
              <button
                key={contractorId}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAssign(contractor);
                }}
                disabled={isAssigning}
                className={cn(
                  "w-full px-3 py-2 text-left hover:bg-surface flex items-center gap-2 transition-colors",
                  isCurrentContractor && "bg-surface border-l-2 border-accent",
                  isAssigning && "opacity-50 cursor-not-allowed"
                )}
              >
                <User className="w-4 h-4 text-text-secondary flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-text-primary truncate">
                    {contractor?.fullName || 'Unknown'}
                    {isCurrentContractor && (
                      <span className="text-accent text-xs ml-1">(current)</span>
                    )}
                  </p>
                  <p className="text-xs text-text-secondary truncate">{contractor?.email}</p>
                </div>
                {isAssigning && (
                  <Loader2 className="w-3 h-3 animate-spin text-text-secondary" />
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Footer hint */}
      <div className="px-3 py-2 border-t border-border bg-background">
        <p className="text-xs text-text-secondary">
          {isBulk
            ? `Click a contractor to assign them to all ${selectedInstanceIds?.length || 0} selected instances`
            : 'Click a contractor to assign them to this instance'
          }
        </p>
      </div>
    </div>
  );
}
